import React, { FC, memo, useCallback, useMemo, useState, useContext, useEffect, useRef } from "react";

// Hooks
import { CartOverlaysContext, useLocalStorageBasedFrequencyCapping } from "@swiggy-private/checkout-core";

// Components
import { PostStateLayout } from "@super-ui/components/m2c-swiggy-one-halfcard/Components/PostState";
import { PostStateLayout as PostStateLayoutBlack } from "@super-ui/components/m2c-swiggy-black-halfcard/Components/PostState";
import { ErrorBoundary, Portal, useEffectOnce } from "@swiggy-private/core-ui";

// DLS
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";

// Types
import { GratificationPopupProps } from "./types";
import { CouponModalIds, GlobalOverlayPriority, OverlayType } from "@swiggy-private/cart-types";

// Styles
import { CloseIcon, PopupContainer, PopupWidth } from "./styles";

// Constants
import {
    DEFAULT_GRATIFICATION_DURATION,
    DEFAULT_GRATIFICATION_DURATION_WITH_LOTTIE,
} from "@super-ui/components/cart-upsell-nudge/constants";
import { GRATIFICATION_CLOSE_BUTTON } from "@super-ui/components/swiggy-one-widget-dls-wrapper/constants";

// Utils
import { isEmpty } from "@swiggy-private/js-utils";
import { isGratificationPopupActive, isGratificationPopupVariantAnimation } from "./helper";

const GratificationPopup: FC<GratificationPopupProps> = ({
    sessionKey,
    currentSid,
    isOneLite,
    isSwiggyBlack = false,
    gratificationComms,
    onGratificationClose,
    analyticsEvents,
}) => {
    const { isSessionValid } = useLocalStorageBasedFrequencyCapping({ key: sessionKey, sessionId: currentSid });
    const isValidSession = useMemo(() => isSessionValid(), [isSessionValid]);

    const [isPopupOpened, setIsPopupOpened] = useState(isValidSession);
    const { onImpressionGratificationPopup, onClickGratificationPopupCta } = analyticsEvents;
    const { registerOverlays, enabledOverlayId, onCloseOverlay } = useContext(CartOverlaysContext);
    const registerGratification = useRef(false);

    useEffectOnce(() => onImpressionGratificationPopup());

    const closeGratificationPopup = useCallback(
        (ctaText: string) => {
            setIsPopupOpened(false);
            onGratificationClose?.();

            onClickGratificationPopupCta(ctaText);
            onCloseOverlay();
        },
        [onClickGratificationPopupCta, onGratificationClose, onCloseOverlay],
    );

    const onClosePopup = useCallback(() => {
        closeGratificationPopup(GRATIFICATION_CLOSE_BUTTON.DISMISS_BUTTON);
    }, [closeGratificationPopup]);

    // Preparing the data for the PostStateLayout component
    const gratificationCommsData = useMemo(() => {
        if (isEmpty(gratificationComms)) return null;

        const {
            title,
            logo_ctx: logoCtx,
            cta,
            sub_titles: subTitles,
            attribute_values: attributeValues,
            header,
            content,
            duration = isGratificationPopupVariantAnimation(gratificationComms.variant)
                ? DEFAULT_GRATIFICATION_DURATION_WITH_LOTTIE
                : DEFAULT_GRATIFICATION_DURATION,
            variant,
        } = gratificationComms;

        return {
            title,
            logoCtx,
            cta,
            subTitles,
            attributeValues,
            headerText: header,
            isOneLite,
            duration,
            content,
            closeBottomSheet: closeGratificationPopup,
            isPopup: true,
            analyticsEvents,
            variant,
        };
    }, [analyticsEvents, closeGratificationPopup, gratificationComms, isOneLite]);

    const postStateComponent = useCallback(() => {
        if (isEmpty(gratificationCommsData)) {
            return;
        }

        if (isSwiggyBlack) {
            return <PostStateLayoutBlack {...gratificationCommsData} />;
        }

        return (
            <PostStateLayout
                {...gratificationCommsData}
                showLottieExp={isGratificationPopupVariantAnimation(gratificationCommsData?.variant)}
            />
        );
    }, [isSwiggyBlack, gratificationCommsData]);

    useEffect(() => {
        if (!isEmpty(gratificationCommsData) && !registerGratification.current && isValidSession) {
            registerOverlays({
                overlayType: OverlayType.MODAL,
                id: CouponModalIds.GRATIFICATION_POPUP,
                priority: GlobalOverlayPriority.GRATIFICATION_POPUP,
            });
            registerGratification.current = true;
        }
    }, [registerOverlays, gratificationCommsData, isValidSession]);

    useEffect(() => {
        if (isGratificationPopupActive(enabledOverlayId)) setIsPopupOpened(true);
    }, [enabledOverlayId]);

    if (isEmpty(gratificationCommsData) || enabledOverlayId !== CouponModalIds.GRATIFICATION_POPUP) return null;

    return (
        <ErrorBoundary>
            <Portal>
                <PopupContainer
                    isOpen={isPopupOpened}
                    closeOnOverlayClick
                    onClose={onClosePopup}
                    hasCustomPadding
                    width={PopupWidth}
                >
                    <CloseIcon onClick={onClosePopup} isSwiggyBlack={isSwiggyBlack}>
                        <SpriteIconWeb iconName="close12" />
                    </CloseIcon>
                    {postStateComponent()}
                </PopupContainer>
            </Portal>
        </ErrorBoundary>
    );
};

export default memo(GratificationPopup);
